import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { ERROR_BASE_DATE } from '~/constants/organization_notification';
import {
  GetUnreadFollowupMessageNotificationsDocument,
  MarkOrganizationNotificationDocument,
} from '~/graphql/generated/graphql';
import { useSubscriptions } from '~/hooks/use-subscriptions';
import { followupMessageErrorState } from '~/state/layouts/SharedAppShell/atoms';
import { FollowupMessageErrorNotification } from '~/state/layouts/SharedAppShell/types';

export const useSubscriptionFollowupMessage = () => {
  const [{ notifications, loaded }, setFollowupMessageErrorState] =
    useAtom(followupMessageErrorState);
  const [event, clear] = useSubscriptions('OrganizationNotification', ['created', 'updated']);
  const [markNotification] = useMutation(MarkOrganizationNotificationDocument);
  const [getNotifications, { data, loading }] = useLazyQuery(
    GetUnreadFollowupMessageNotificationsDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (loading) return;
    if (!data?.me || data.me.__typename !== 'Practitioner') return;

    const organizationNotifications = data.me.organization.unreadOrganizationNotifications.nodes;

    const notifications = organizationNotifications
      .filter((n) => !!n.medicationFollowupMessage)
      .map((n) => {
        return {
          followupMessage: n.medicationFollowupMessage || '',
          organizationNotificationId: n.id,
          type: n.type,
          createdAt: n.createdAt,
        } as FollowupMessageErrorNotification;
      });

    setFollowupMessageErrorState((_state) => ({
      ..._state,
      notifications,
      loaded: true,
    }));
  }, [data, loading, setFollowupMessageErrorState]);

  const handleClose = useCallback(
    async (followupMessageId: string) => {
      const newNotifications = notifications.filter(
        (notification) => notification.followupMessage.id !== followupMessageId,
      );
      setFollowupMessageErrorState((_state) => ({ ..._state, notifications: newNotifications }));

      const notificationId = notifications.find(
        (n) => n.followupMessage.id === followupMessageId,
      )?.organizationNotificationId;

      if (notificationId) {
        await markNotification({
          variables: {
            input: {
              organizationNotificationId: notificationId,
            },
          },
        });
      }
    },
    [markNotification, notifications, setFollowupMessageErrorState],
  );

  useEffect(() => {
    if (event) {
      getNotifications({ variables: { from: ERROR_BASE_DATE } });
      clear();
    }
  }, [clear, event, getNotifications]);

  useEffect(() => {
    if (!loaded) {
      getNotifications({ variables: { from: ERROR_BASE_DATE } });
    }
  }, [getNotifications, loaded]);

  return {
    handleClose,
  };
};
